import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, eventQuery } from "../api";
import type { WhatsappChat, WhatsappMessage } from "../types";
import { Modal } from "./Modal";
import { ParticipantAvatar } from "./ParticipantAvatar";
import { WhatsappIcon } from "./WhatsappIcon";
import { IconActionButton } from "./AdminActions";

type Props = {
  eventId: number;
  speakerId: number;
  refreshToken: number;
  realtimeConnected: boolean;
  onClose: () => void;
  onInboxChange: () => void;
  onToast: (message: string) => void;
};

export function WhatsappChatModal({ eventId, speakerId, refreshToken, realtimeConnected, onClose, onInboxChange, onToast }: Props) {
  const [chat, setChat] = useState<WhatsappChat | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const next = await api<WhatsappChat>(`/api/speakers/${speakerId}/whatsapp?${eventQuery(eventId)}`);
      setChat(next);
      if (next.conversation.unreadCount) {
        await api(`/api/speakers/${speakerId}/whatsapp/read`, { method: "POST", body: JSON.stringify({ eventId }) });
        onInboxChange();
      }
    } catch (error) {
      if (!silent) onToast(error instanceof Error ? error.message : "No se pudo cargar la conversación");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [eventId, onInboxChange, onToast, speakerId]);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      await load();
      try {
        await api(`/api/speakers/${speakerId}/whatsapp/sync`, { method: "POST", body: JSON.stringify({ eventId }) });
        if (active) await load(true);
      } catch { /* La escucha en vivo y el sondeo continúan aunque no haya historial disponible. */ }
    };
    initialize();
    const fallbackTimer = window.setInterval(() => load(true), 15_000);
    const refreshVisible = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", refreshVisible);
    return () => { active = false; window.clearInterval(fallbackTimer); document.removeEventListener("visibilitychange", refreshVisible); };
  }, [eventId, load, speakerId]);

  useEffect(() => { if (refreshToken) load(true); }, [load, refreshToken]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [chat?.messages.length]);

  const timeline = useMemo(() => {
    if (!chat) return [];
    const messages = chat.messages.map((message) => ({ kind: "message" as const, id: `message-${message.id}`, occurredAt: message.occurredAt, message }));
    const communications = chat.communicationHistory
      .filter((delivery) => !chat.messages.some((message) => message.communicationId === delivery.communicationId))
      .map((delivery) => ({ kind: "communication" as const, id: `communication-${delivery.id}`, occurredAt: delivery.occurredAt, delivery }));
    return [...messages, ...communications].sort((first, second) => first.occurredAt.localeCompare(second.occurredAt));
  }, [chat]);

  async function send(event: FormEvent) {
    event.preventDefault();
    const message = draft.trim();
    if (!message) return;
    setSending(true);
    setDraft("");
    try {
      const result = await api<{ message: WhatsappMessage | null }>(`/api/speakers/${speakerId}/whatsapp/message`, { method: "POST", body: JSON.stringify({ eventId, message }) });
      if (result.message) setChat((current) => current && !current.messages.some((item) => item.id === result.message?.id) ? { ...current, messages: [...current.messages, result.message as WhatsappMessage] } : current);
      await load();
      onInboxChange();
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return ;
      setDraft((current) => current || message);
      onToast(error instanceof Error ? error.message : "No se pudo enviar el mensaje");
    } finally {
      setSending(false);
    }
  }

  const speaker = chat?.speaker;
  return <Modal title={speaker ? `${speaker.firstName} ${speaker.lastName}` : "Conversación de WhatsApp"} eyebrow="WhatsApp del faculty" wide className="modal-whatsapp" onClose={onClose}>
    <div className="whatsapp-chat">
      {speaker && <header className="whatsapp-chat-contact"><ParticipantAvatar name={`${speaker.firstName} ${speaker.lastName}`} country={speaker.country} photoUrl={speaker.photoUrl} /><div><strong>{speaker.firstName} {speaker.lastName}</strong><span>{speaker.phone || "Sin teléfono configurado"}</span></div><span className={`whatsapp-connection ${chat.status.status === "ready" ? "ready" : ""}`}>{chat.status.status === "ready" ? "WhatsApp conectado" : "No vinculado"}</span><span className={`whatsapp-realtime ${realtimeConnected ? "ready" : ""}`}><i />{realtimeConnected ? "Actualización en vivo" : "Reconectando actualizaciones"}</span></header>}
      <div className="whatsapp-thread" aria-live="polite">
        {loading && <div className="skeleton page-skeleton short" />}
        {!loading && !timeline.length && <div className="whatsapp-empty"><span><WhatsappIcon /></span><h3>Sin mensajes todavía</h3><p>Escribí el primer mensaje o enviá una comunicación al faculty.</p></div>}
        {timeline.map((item) => item.kind === "message" ? <MessageBubble message={item.message} key={item.id} /> : <div className={`whatsapp-communication-event ${item.delivery.status === "sent" ? "" : "error"}`} key={item.id}><span>{item.delivery.status === "sent" ? "Comunicación enviada" : "Comunicación no enviada"}</span><strong>{item.delivery.subject}</strong><p>{item.delivery.body}</p><small>{formatChatDate(item.delivery.occurredAt)} · {item.delivery.status === "sent" ? "Entregada" : item.delivery.errorMessage || item.delivery.status}</small></div>)}
        <div ref={bottomRef} />
      </div>
      <form className="whatsapp-composer" onSubmit={send}><textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={2} maxLength={4000} placeholder={speaker?.phone ? "Escribí un mensaje…" : "El faculty no tiene teléfono"} disabled={!speaker?.phone || chat?.status.status !== "ready"} aria-label="Mensaje de WhatsApp" /><IconActionButton label="Enviar WhatsApp" icon="send" tone="success" type="submit" busy={sending} disabled={!draft.trim() || sending || !speaker?.phone || chat?.status.status !== "ready"} /></form>
    </div>
  </Modal>;
}

function MessageBubble({ message }: { message: WhatsappMessage }) {
  return <article className={`whatsapp-bubble ${message.direction}`}><div>{message.body && <p>{message.body}</p>}{message.mediaName && <a className="whatsapp-attachment" href={message.mediaUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!message.mediaUrl}><span>▣</span><div><strong>{message.mediaName}</strong><small>{message.mediaMime || "Archivo de WhatsApp"}</small></div></a>}</div><time>{formatChatDate(message.occurredAt)}{message.direction === "outgoing" ? " · ✓✓" : ""}</time></article>;
}

function formatChatDate(value: string) {
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}
